import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Sets a user as a super admin by their email
 * This function should only be used during initial setup or by existing super admins
 * @param {string} email - The email of the user to make super admin
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const setupSuperAdmin = async (email) => {
  try {
    // First, find the user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { 
        success: false, 
        message: `No user found with email: ${email}` 
      };
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    if (userData.superAdmin === true) {
      return { 
        success: true, 
        message: `User ${email} is already a super admin` 
      };
    }
    
    // Update the user to be a super admin
    await updateDoc(doc(db, 'users', userDoc.id), {
      superAdmin: true,
      updatedAt: new Date(),
      updatedBy: 'system-setup'
    });
    
    return { 
      success: true, 
      message: `Successfully made ${email} a super admin` 
    };
    
  } catch (error) {
    console.error('Error setting up super admin:', error);
    return { 
      success: false, 
      message: `Error: ${error.message}` 
    };
  }
};

/**
 * Removes super admin status from a user
 * @param {string} email - The email of the user to remove super admin from
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const removeSuperAdmin = async (email) => {
  try {
    // First, find the user by email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { 
        success: false, 
        message: `No user found with email: ${email}` 
      };
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    if (userData.superAdmin !== true) {
      return { 
        success: true, 
        message: `User ${email} is not a super admin` 
      };
    }
    
    // Remove super admin status
    await updateDoc(doc(db, 'users', userDoc.id), {
      superAdmin: false,
      updatedAt: new Date(),
      updatedBy: 'system-setup'
    });
    
    return { 
      success: true, 
      message: `Successfully removed super admin status from ${email}` 
    };
    
  } catch (error) {
    console.error('Error removing super admin:', error);
    return { 
      success: false, 
      message: `Error: ${error.message}` 
    };
  }
};

/**
 * Gets all super admin users
 * @returns {Promise<Array>} Array of super admin users
 */
export const getSuperAdmins = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('superAdmin', '==', true));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error('Error getting super admins:', error);
    return [];
  }
};
